import { MatchInfo, TimerConfig, ColorPalette, BrandingConfig } from '../types';

// Mock data for match information
export const MOCK_MATCH_DATA: MatchInfo = {
  matchId: 'match_001',
  competitionName: 'Championship Open',
  categoryName: 'Men Senior',
  drawName: 'Main Draw',
  refereeName: 'John Smith',
  dateTime: '2025-06-14 14:30',
  scoringType: 'BestOf5', // or 'BestOf3'
  playerA: 'Player A',
  playerB: 'Player B',
  roundType: 'Quarter Finals'
  // Les champs liveScore (context, centerName, centerId, courtName, courtId)
  // sont optionnels quand liveScoreUrl n'est pas utilisé
};

// Unified timer configuration
export const TIMERS: Record<string, TimerConfig> = {
  warmup:        { id: 'warmup',       name: 'Warmup (4\')',               duration: 240, broadcastId: 'warmup',       showHalftime: true, showQuiz: true },
  before_game:   { id: 'beforeGame',   name: 'Before game (60")',          duration: 60,  broadcastId: 'beforeGame' },
  between_games: { id: 'betweenGames', name: 'Before game (2\')',          duration: 120, broadcastId: 'betweenGames' },
  equipment:     { id: 'equipment',    name: 'Equipment change (2\')',     duration: 120, broadcastId: 'equipment',    selectable: true },
  self_injury:   { id: 'injury',       name: 'Self-injury (3\')',          duration: 180, broadcastId: 'injury',       selectable: true },
  self_bleeding: { id: 'bleeding',     name: 'Self-bleeding (5\')',        duration: 300, broadcastId: 'bleeding',     selectable: true },
  shared:        { id: 'shared',       name: 'Shared-responsibility (15\')', duration: 900, broadcastId: 'shared',    selectable: true },
};

// Default colors - 6 couleurs × 4 tons (foncé à clair)
export const COLOR_PALETTE: ColorPalette = {
  red: ['#7F1D1D', '#DC2626', '#EF4444', '#FCA5A5'],
  blue: ['#1E3A8A', '#2563EB', '#3B82F6', '#93C5FD'],
  yellow: ['#A16207', '#D97706', '#F59E0B', '#FDE047'],
  black: ['#000000', '#374151', '#6B7280', '#D1D5DB'],
  green: ['#14532D', '#16A34A', '#22C55E', '#86EFAC'],
  purple: ['#581C87', '#9333EA', '#A855F7', '#D8B4FE']
};

// Default player colors
export const DEFAULT_PLAYER_COLORS = {
  A: '#3B82F6', // Blue Light
  B: '#EF4444'  // Red Light
};

// Helper: selectable timers for injury/equipment modal
export const SELECTABLE_TIMERS = Object.values(TIMERS).filter(t => t.selectable);

export const CONDUCT_RULES = [
  {
    id: '14.6.1',
    title: 'Audible or visual obscenity',
    minor: '• Muttered swearing, blasphemy',
    major: '• Audible obscenity',
    game: '• Extreme behavior bringing the game into disrepute\n• Penalty level to be assessed based on severity',
    match: ''
  },
  {
    id: '14.6.2',
    title: 'Unsporting behavior',
    minor: '• Derogatory comments to opponent',
    major: '• Repeated or pejorative derogatory comments to opponent\n• Attempt to intimidate opponent',
    game: '• Extreme behavior bringing the game into disrepute\n• Penalty level to be assessed based on severity',
    match: ''
  },
  {
    id: '14.6.3',
    title: 'Excessive physical contact',
    minor: '• Colliding with opponent',
    major: '• Excessive contact with opponent',
    game: '• Deliberate injury to opponent\n• Dangerous play resulting in injury to opponent\n• Penalty level to be assessed based on severity and injury caused',
    match: ''
  },
  {
    id: '14.6.4',
    title: 'Deliberate or dangerous action',
    minor: '• Excessive swing without injury or turning',
    major: '• Excessive swing  or turning resulting in injury to opponent',
    game: '',
    match: ''
  },
  {
    id: '14.6.5',
    title: 'Dissent',
    minor: '• Arguing a decision',
    major: '• Arguing or contesting a decision in a prolonged or repeated manner',
    game: '',
    match: ''
  },
  {
    id: '14.6.6',
    title: 'Attempting to influence the Referee',
    minor: '• Shouting what you want awarded during rallies\n• Making comments during rallies\n• Making comments during rallies\n • Questioning, explaining complaining, or arguing to influence a decision\n • Repeated requests for explanations',
    major: '• Deliberate simulation, exaggeration of interference, or attempt to influence decision\n• Aggressive or confrontational behavior directed at influencing decisions\n• Persistent arguing after being warned\n• Deliberately creating situations (like wet areas) to manipulate referee decisions\n • Direct challenges to referee authority or competence',
    game: '',
    match: ''
  },
  {
    id: '14.6.7',
    title: 'Equipment abuse (racket or ball or other equipment)',
    minor: '• Throwing racket on floor after losing a game\n• Hitting ball hard at end of rally',
    major: '• Deliberately breaking racket\n• Throwing racket at wall\n• Hitting ball or throwing racket out of court',
    game: '',
    match: ''
  },
  {
    id: '14.6.8',
    title: 'Unfair warm-up',
    minor: '• Extending ball rallies beyond 3 shots\n• Few seconds delay returning to court',
    major: '',
    game: '',
    match: ''
  },
  {
    id: '14.6.9',
    title: 'Time wasting',
    minor: '• Excessive time to serve',
    major: '• Very late returning to court\n• Prolonged discussion with Referee',
    game: '',
    match: ''
  },
  {
    id: '14.6.10',
    title: 'Deliberate distraction',
    minor: '• Deliberately positioning oneself in opponent\'s path to prevent movement to ball\n• Touching opponent to prevent movement or stroke\n• Making sudden movements or direction changes that may unsettle opponent',
    major: '• Making noises or gestures that disturb opponent\n• Attempting to steal opponent\'s racket',
    game: '',
    match: ''
  },
  {
    id: '14.6.11',
    title: 'Coaching during play',
    minor: '• First time',
    major: '• Second time\n• Ejection of person concerned',
    game: '',
    match: ''
  },
  {
    id: '14.4',
    title: 'Abuse towards Referee',
    minor: '• Unflattering comments about Referee or decisions',
    major: '• Derogatory comments about Referee or decisions\n• Request for change of official/referee',
    game: '• Throwing ball towards Referee or spectators\n• Throwing racket towards Referee or spectators\n• Penalty level to be assessed based on severity and danger caused',
    match: ''
  }
] as const;


// 🔄 Configuration du système de rollback pour la compression storage
export const ROLLBACK_CONFIG = {
  // Nombre max de rollbacks à conserver en storage compressé
  // null = illimité (garde tous les rollbacks)
  // nombre = limite pour optimiser la taille storage
  MAX_ROLLBACKS_IN_STORAGE: null as number | null,
  //MAX_ROLLBACKS_IN_STORAGE: 15 as number | null,

  // Taille max du storage en KB avant activation de la limite automatique
  AUTO_LIMIT_THRESHOLD_KB: 100,
  
  // Limite de fallback si le storage devient trop gros
  FALLBACK_LIMIT: 20
} as const;

// 🎛️ Presets de configuration faciles à modifier
export const ROLLBACK_PRESETS = {
  UNLIMITED: null,           // Pas de limite - garde tout
  CONSERVATIVE: 5,           // Très économe en storage
  BALANCED: 15,              // Équilibre performance/storage
  GENEROUS: 30,              // Beaucoup de rollbacks
  POWER_USER: 50             // Pour utilisateurs avancés
} as const;

/**
 * Pattern pour identifier les matchs créés manuellement via MatchSetupScreen
 * Les matchs manuels ne doivent pas avoir de bouton de validation dans AfterMatch
 */
export const MANUAL_MATCH_PREFIX = 'MANUAL-';

/**
 * Clé localStorage pour les préférences du formulaire de setup manuel
 * Utilisée pour persister le nom de l'arbitre entre les sessions
 */
export const MANUAL_SETUP_STORAGE_KEY = 'manualSetupPreferences';

/**
 * Drapeau sessionStorage posé lors de la transition vers AfterMatch en fin de
 * match. Consommé (et effacé) par AfterMatch pour rejouer l'annonce de fin une
 * seule fois, sans la rejouer lors d'un rechargement de page (recovery).
 */
export const MATCH_END_ANNOUNCEMENT_FLAG = 'matchEndAnnouncementPending';

// 🎨 Configuration du branding par défaut
export const DEFAULT_BRANDING: Required<Omit<BrandingConfig, 'name' | 'logo2' | 'sponsorLogos'>> = {
  logo1: './images/logo.png',
  primaryColor: '#4E4C4B',
  secondaryColor: '#FFFFFF',
  favicon: './favicon.ico'
};